import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { AppConfig } from "../AppConfig";
import { User } from "../ducks/auth/types";

// We'll need to import the store to dispatch actions
// This will be set by the app initialization
let dispatch: any = null;

export const setStore = (appDispatch: any) => {
    dispatch = appDispatch;
};

/**
 * Handles logout by clearing all authentication data and redirecting to login
 * This function can be called from anywhere in the app when authentication fails
 */
export const handleAuthLogout = () => {
    // Clear all tokens and user data
    LocalStorageManager.Instance.removeObject(AppConfig.Instance.LocalStorageKey["accessToken"]);
    LocalStorageManager.Instance.removeObject(AppConfig.Instance.LocalStorageKey["refreshToken"]);
    LocalStorageManager.Instance.removeObject(AppConfig.Instance.LocalStorageKey["loggedInUser"]);

    // Navigate to login page
    // We'll use window.location to ensure a full page reload and clear any cached state
    // This ensures that all Redux state is cleared and the user is properly redirected
    window.location.href = "/login";
};

/**
 * Shows session expired modal
 * This function shows a dialog to inform the user that their session has expired
 */
export const handleSessionExpired = () => {
    if (dispatch) {
        // Show the session expired modal
        dispatch({
            type: '@@App/SHOW_SESSION_EXPIRED_MODAL'
        });
    } else {
        // Fallback to immediate logout if store is not available
        handleAuthLogout();
    }
};

/**
 * Confirms session expiration and redirects to login
 * This function is called when user clicks "Log In Again" in the modal
 */
export const confirmSessionExpired = () => {
    if (dispatch) {
        // Hide the modal
        dispatch({ type: '@@App/HIDE_SESSION_EXPIRED_MODAL' });
    }
    // Reset the modal flag
    const { resetSessionExpiredModalFlag } = require('../core/http/httpClient');
    resetSessionExpiredModalFlag();
    // Redirect to login
    handleAuthLogout();
};

/**
 * Updates local storage with new user data if the updated member is the current logged-in user
 * @param updatedMemberData - The updated member data from API response
 * @param currentUser - The current logged-in user from local storage
 * @returns boolean - True if local storage was updated, false otherwise
 */
export const updateLocalStorageIfCurrentUser = (
    updatedMemberData: {
        id: string;
        username: string;
        email: string;
        role: any;
        last_login: string;
    },
    currentUser: User | null
): boolean => {
    // Check if the updated member is the current logged-in user
    if (currentUser && currentUser.email === updatedMemberData.email) {
        // Update local storage with new user data
        const updatedUserData: User = {
            id: updatedMemberData.id,
            username: updatedMemberData.username,
            email: updatedMemberData.email,
            role: updatedMemberData.role,
            last_login: updatedMemberData.last_login,
        };

        LocalStorageManager.Instance.saveObject(
            AppConfig.Instance.LocalStorageKey["loggedInUser"],
            updatedUserData
        );

        return true;
    }

    return false;
};

/**
 * Gets the current logged-in user from local storage
 * @returns User | null - The current user or null if not found
 */
export const getCurrentUserFromStorage = (): User | null => {
    return LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["loggedInUser"]
    );
};


/**
 * Checks if the user is currently authenticated
 * @returns boolean indicating if user is logged in
 */
export const isAuthenticated = (): boolean => {
    const accessToken = LocalStorageManager.Instance.getObject<string>(
        AppConfig.Instance.LocalStorageKey["accessToken"]
    );
    return !!accessToken;
};

/**
 * Gets the current access token
 * @returns the access token or null if not found
 */
export const getAccessToken = (): string | null => {
    return LocalStorageManager.Instance.getObject<string>(
        AppConfig.Instance.LocalStorageKey["accessToken"]
    );
};


